import React, { useState, useEffect, useCallback } from "react";
import { useTheme } from "@mui/material/styles";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Switch,
	TextField,
	Typography,
	CircularProgress,
} from "@mui/material";
import { DeleteOutlined, Edit, Check, Close } from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles()((theme) => ({
	fila: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		paddingLeft: 0,
		paddingRight: 0,
	},
	inactivo: {
		"& .MuiListItemText-primary": {
			color: theme.palette.text.disabled,
			textDecoration: "line-through",
		},
	},
	campoNuevo: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "flex-start",
		marginBottom: theme.spacing(1),
	},
	vacio: {
		color: theme.palette.text.secondary,
		fontSize: 13.5,
		padding: theme.spacing(2, 0),
		textAlign: "center",
	},
	selectCategoria: {
		border: "none",
		outline: "none",
		background: "transparent",
		color: theme.palette.text.secondary,
		fontSize: 12.5,
		fontFamily: theme.typography.fontFamily,
		cursor: "pointer",
		maxWidth: 130,
	},
	bloqueCategorias: {
		marginBottom: theme.spacing(2),
		paddingBottom: theme.spacing(1.5),
		borderBottom: `1px solid ${theme.palette.mode === "light" ? "#e1e0d9" : "#2c2c2a"}`,
	},
	tituloBloque: {
		fontSize: 12,
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
	chips: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.75),
	},
	chip: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		fontSize: 12.5,
		padding: theme.spacing(0.25, 0.75),
		borderRadius: 12,
		border: `1px solid ${theme.palette.mode === "light" ? "#c3c2b7" : "#383835"}`,
		color: theme.palette.text.secondary,
	},
	nota: {
		fontSize: 12.5,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(1.5),
		lineHeight: 1.5,
	},
	cargando: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3, 0),
	},
}));

const CloseReasonModal = ({ open, onClose }) => {
	const { classes, cx } = useStyles();
	const theme = useTheme();

	const [motivos, setMotivos] = useState([]);
	const [cargando, setCargando] = useState(false);
	const [nombreNuevo, setNombreNuevo] = useState("");
	const [guardando, setGuardando] = useState(false);
	const [editandoId, setEditandoId] = useState(null);
	const [nombreEditado, setNombreEditado] = useState("");
	const [porBorrar, setPorBorrar] = useState(null);
	const [categorias, setCategorias] = useState([]);
	const [categoriaNueva, setCategoriaNueva] = useState("");

	const cargar = useCallback(async () => {
		setCargando(true);
		try {
			const { data } = await api.get("/close-reason");
			setMotivos(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		} finally {
			setCargando(false);
		}
	}, []);

	const cargarCategorias = useCallback(async () => {
		try {
			const { data } = await api.get("/close-reason-category");
			setCategorias(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const handleCrearCategoria = async () => {
		const nombre = categoriaNueva.trim();
		if (nombre.length < 2) return;
		try {
			await api.post("/close-reason-category", { name: nombre });
			setCategoriaNueva("");
			await cargarCategorias();
		} catch (err) {
			toastError(err);
		}
	};

	const handleBorrarCategoria = async (id) => {
		try {
			await api.delete(`/close-reason-category/${id}`);
			await Promise.all([cargarCategorias(), cargar()]);
		} catch (err) {
			toastError(err);
		}
	};

	const handleCambioCategoria = async (motivoId, valor) => {
		try {
			await api.put(`/close-reason/${motivoId}`, { categoryId: valor === "" ? null : Number(valor) });
			await cargar();
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		if (open) {
			cargar();
			cargarCategorias();
			setNombreNuevo("");
			setEditandoId(null);
		}
	}, [open, cargar]);

	const handleCrear = async () => {
		const nombre = nombreNuevo.trim();
		if (nombre.length < 2) return;
		setGuardando(true);
		try {
			await api.post("/close-reason", { name: nombre });
			setNombreNuevo("");
			toast.success(i18n.t("closeReasonModal.toasts.created"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setGuardando(false);
		}
	};

	const handleRenombrar = async (id) => {
		const nombre = nombreEditado.trim();
		if (nombre.length < 2) return;
		setGuardando(true);
		try {
			await api.put(`/close-reason/${id}`, { name: nombre });
			setEditandoId(null);
			toast.success(i18n.t("closeReasonModal.toasts.updated"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setGuardando(false);
		}
	};

	const handleActivar = async (motivo) => {
		try {
			await api.put(`/close-reason/${motivo.id}`, { active: !motivo.active });
			await cargar();
		} catch (err) {
			toastError(err);
		}
	};

	const handleBorrar = async () => {
		if (!porBorrar) return;
		try {
			await api.delete(`/close-reason/${porBorrar.id}`);
			toast.success(i18n.t("closeReasonModal.toasts.deleted"));
			await cargar();
		} catch (err) {
			toastError(err);
		} finally {
			setPorBorrar(null);
		}
	};

	return (
		<>
			<ConfirmationModal
				title={i18n.t("closeReasonModal.confirmDelete.title")}
				open={Boolean(porBorrar)}
				onClose={() => setPorBorrar(null)}
				onConfirm={handleBorrar}
			>
				{i18n.t("closeReasonModal.confirmDelete.message")}
			</ConfirmationModal>

			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
				<DialogTitle>{i18n.t("closeReasonModal.title")}</DialogTitle>
				<DialogContent dividers>
					{}
					<div className={classes.bloqueCategorias}>
						<Typography className={classes.tituloBloque}>
							{i18n.t("closeReasonModal.categories.title")}
						</Typography>
						<div className={classes.campoNuevo}>
							<TextField
								label={i18n.t("closeReasonModal.categories.name")}
								value={categoriaNueva}
								onChange={(e) => setCategoriaNueva(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleCrearCategoria();
									}
								}}
								variant="outlined"
								margin="dense"
								size="small"
								fullWidth
							/>
							<Button
								variant="outlined"
								color="primary"
								onClick={handleCrearCategoria}
								disabled={categoriaNueva.trim().length < 2}
								style={{ marginTop: 8 }}
							>
								{i18n.t("closeReasonModal.buttons.add")}
							</Button>
						</div>
						{categorias.length > 0 && (
							<div className={classes.chips}>
								{categorias.map((c) => (
									<span key={c.id} className={classes.chip}>
										{c.name}
										<IconButton size="small" onClick={() => handleBorrarCategoria(c.id)}>
											<Close style={{ fontSize: 13 }} />
										</IconButton>
									</span>
								))}
							</div>
						)}
					</div>

					<div className={classes.campoNuevo}>
						<TextField
							label={i18n.t("closeReasonModal.form.name")}
							value={nombreNuevo}
							onChange={(e) => setNombreNuevo(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleCrear();
								}
							}}
							variant="outlined"
							margin="dense"
							size="small"
							fullWidth
							autoFocus
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={handleCrear}
							disabled={guardando || nombreNuevo.trim().length < 2}
							style={{ marginTop: 8 }}
						>
							{i18n.t("closeReasonModal.buttons.add")}
						</Button>
					</div>

					{cargando ? (
						<div className={classes.cargando}>
							<CircularProgress size={22} />
						</div>
					) : motivos.length === 0 ? (
						<Typography className={classes.vacio}>
							{i18n.t("closeReasonModal.empty")}
						</Typography>
					) : (
						<>
							<List dense>
								{motivos.map((motivo) => (
									<ListItem key={motivo.id} className={classes.fila} divider>
										{editandoId === motivo.id ? (
											<>
												<TextField
													value={nombreEditado}
													onChange={(e) => setNombreEditado(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === "Enter") {
															e.preventDefault();
															handleRenombrar(motivo.id);
														}
														if (e.key === "Escape") setEditandoId(null);
													}}
													variant="outlined"
													size="small"
													fullWidth
													autoFocus
												/>
												<IconButton
													size="small"
													onClick={() => handleRenombrar(motivo.id)}
													disabled={guardando}
												>
													<Check fontSize="small" />
												</IconButton>
												<IconButton size="small" onClick={() => setEditandoId(null)}>
													<Close fontSize="small" />
												</IconButton>
											</>
										) : (
											<>
												<Switch
													size="small"
													checked={Boolean(motivo.active)}
													onChange={() => handleActivar(motivo)}
												/>
												<ListItemText
													className={cx(!motivo.active && classes.inactivo)}
													primary={motivo.name}
												/>
												<select
													className={classes.selectCategoria}
													style={{ colorScheme: theme.palette.mode }}
													value={motivo.categoryId || ""}
													onChange={(e) => handleCambioCategoria(motivo.id, e.target.value)}
												>
													<option value="">
														{i18n.t("closeReasonModal.categories.none")}
													</option>
													{categorias.map((c) => (
														<option key={c.id} value={c.id}>
															{c.name}
														</option>
													))}
												</select>
												<IconButton
													size="small"
													onClick={() => {
														setEditandoId(motivo.id);
														setNombreEditado(motivo.name);
													}}
												>
													<Edit fontSize="small" />
												</IconButton>
												<IconButton size="small" onClick={() => setPorBorrar(motivo)}>
													<DeleteOutlined fontSize="small" />
												</IconButton>
											</>
										)}
									</ListItem>
								))}
							</List>
							<Typography className={classes.nota}>
								{i18n.t("closeReasonModal.note")}
							</Typography>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} color="secondary" variant="outlined">
						{i18n.t("closeReasonModal.buttons.close")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CloseReasonModal;
